import { motion, useInView } from "framer-motion";
import { useRef, useState, useEffect, useCallback } from "react";
import { ArrowRight } from "lucide-react";

const TRIAL_URL = "https://6vmun24i.forms.app/plumbing-lead-qualification-form";
const DEMO_URL = "https://cal.com/noah-velocitylabs";

function useCountUp(target, duration = 400) {
  const [display, setDisplay] = useState(target);
  const rafRef = useRef(null);
  const startRef = useRef(null);
  const fromRef = useRef(target);

  useEffect(() => {
    const from = fromRef.current;
    const to = target;
    if (from === to) return;

    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    startRef.current = null;

    const step = (timestamp) => {
      if (!startRef.current) startRef.current = timestamp;
      const elapsed = timestamp - startRef.current;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      const current = Math.round(from + (to - from) * eased);
      setDisplay(current);

      if (progress < 1) {
        rafRef.current = requestAnimationFrame(step);
      } else {
        fromRef.current = to;
      }
    };

    rafRef.current = requestAnimationFrame(step);
    return () => {if (rafRef.current) cancelAnimationFrame(rafRef.current);};
  }, [target, duration]);

  return display;
}

function SliderInput({ label, value, onChange, min, max, step, suffix }) {
  const pct = (value - min) / (max - min) * 100;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <label className="text-sm text-[#A3A3A3] font-medium" style={{ fontFamily: "Manrope, sans-serif" }}>{label}</label>
        <span className="text-sm font-semibold text-[#5B8DEF]" style={{ fontFamily: "Manrope, sans-serif" }}>{value}{suffix}</span>
      </div>
      <div className="relative h-10 flex items-center">
        <div className="absolute left-0 right-0 h-1.5 rounded-full bg-[#1A1A1A]">
          <div className="h-full rounded-full bg-[#5B8DEF]/40" style={{ width: `${pct}%` }}></div>
        </div>
        <input
          type="range"
          min={min}
          max={max}
          step={step}
          value={value}
          onChange={(e) => onChange(Number(e.target.value))}
          data-testid={`slider-${label.toLowerCase().replace(/[^a-z]/g, "-")}`}
          className="calc-slider absolute left-0 right-0 w-full appearance-none bg-transparent cursor-pointer z-10"
          style={{ height: "24px" }} />

      </div>
    </div>);

}

export default function CalculatorSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-80px" });

  const [missedCalls, setMissedCalls] = useState(10);
  const [missedCallsRaw, setMissedCallsRaw] = useState("10");
  const [jobValue, setJobValue] = useState(450);
  const [jobValueRaw, setJobValueRaw] = useState("450");
  const [closeRate, setCloseRate] = useState(40);
  const calculate = useCallback(() => {
    const cr = closeRate / 100;
    const lostJobsPerWeek = missedCalls * cr;
    const lostJobsPerYear = Math.round(lostJobsPerWeek * 52);
    const annualRevenueLost = Math.round(lostJobsPerYear * jobValue);
    const dailyLost = Math.round(annualRevenueLost / 365);
    return { lostJobsPerYear, annualRevenueLost, dailyLost };
  }, [missedCalls, jobValue, closeRate]);

  const results = calculate();
  const animRevenue = useCountUp(results.annualRevenueLost);
  const animJobs = useCountUp(results.lostJobsPerYear);
  const animDaily = useCountUp(results.dailyLost);

  return (
    <section
      id="calculator"
      data-testid="calculator-section"
      className="relative py-24 md:py-32 px-6 md:px-12"
      style={{ backgroundColor: "#0A0A0A" }}>

      <div className="max-w-7xl mx-auto" ref={ref}>
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5 }}
          className="text-center mb-16">

          <span
            className="inline-flex items-center gap-2 text-sm uppercase tracking-widest text-[#5B8DEF] font-medium mb-4"
            style={{ fontFamily: "Manrope, sans-serif" }}
            data-testid="calculator-label">

            <span className="w-6 h-px bg-[#5B8DEF]"></span>
            The Real Cost
            <span className="w-6 h-px bg-[#5B8DEF]"></span>
          </span>
          <h2
            className="text-3xl md:text-4xl tracking-tight font-semibold text-white mb-4"
            style={{ fontFamily: "Outfit, sans-serif" }}
            data-testid="calculator-headline">

            See Exactly What You're Losing.
          </h2>
          <p
            className="text-base text-[#A3A3A3] max-w-lg mx-auto"
            style={{ fontFamily: "Manrope, sans-serif" }}>

            Enter your numbers below. Most plumbers are shocked by the result.
          </p>
        </motion.div>

        {/* Calculator Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5, delay: 0.15 }}
          data-testid="calculator-card"
          className="bg-[#111111] border border-[#1e1e1e] rounded-xl p-6 md:p-8">

          <div className="grid md:grid-cols-2 gap-8 md:gap-12">
            {/* Left — Inputs */}
            <div className="space-y-7" data-testid="calculator-inputs">
              {/* Missed calls per week */}
              <div className="space-y-2">
                <label className="text-sm text-[#A3A3A3] font-medium block" style={{ fontFamily: "Manrope, sans-serif" }}>
                  Missed calls per week
                </label>
                <input
                  type="number"
                  min={1}
                  max={200}
                  value={missedCallsRaw}
                  onChange={(e) => {
                    setMissedCallsRaw(e.target.value);
                    const num = Number(e.target.value);
                    if (!isNaN(num) && num >= 1) setMissedCalls(Math.min(200, num));
                  }}
                  onBlur={() => {
                    const clamped = Math.max(1, Math.min(200, Number(missedCallsRaw) || 1));
                    setMissedCalls(clamped);
                    setMissedCallsRaw(String(clamped));
                  }}
                  data-testid="input-missed-calls"
                  className="w-full bg-[#1A1A1A] border border-white/[0.08] rounded-lg px-4 py-3 text-white text-sm font-medium outline-none focus:border-[#5B8DEF]/50 transition-colors"
                  style={{ fontFamily: "Manrope, sans-serif" }} />

              </div>

              {/* Average job value */}
              <div className="space-y-2">
                <label className="text-sm text-[#A3A3A3] font-medium block" style={{ fontFamily: "Manrope, sans-serif" }}>
                  Average job value ($)
                </label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-[#A3A3A3] text-sm font-medium" style={{ fontFamily: "Manrope, sans-serif" }}>$</span>
                  <input
                    type="number"
                    min={50}
                    max={10000}
                    value={jobValueRaw}
                    onChange={(e) => {
                      setJobValueRaw(e.target.value);
                      const num = Number(e.target.value);
                      if (!isNaN(num) && num >= 1) setJobValue(Math.min(10000, num));
                    }}
                    onBlur={() => {
                      const clamped = Math.max(50, Math.min(10000, Number(jobValueRaw) || 50));
                      setJobValue(clamped);
                      setJobValueRaw(String(clamped));
                    }}
                    data-testid="input-job-value"
                    className="w-full bg-[#1A1A1A] border border-white/[0.08] rounded-lg pl-8 pr-4 py-3 text-white text-sm font-medium outline-none focus:border-[#5B8DEF]/50 transition-colors"
                    style={{ fontFamily: "Manrope, sans-serif" }} />

                </div>
              </div>

              {/* Close rate slider */}
              <SliderInput
                label="Your close rate on answered calls"
                value={closeRate}
                onChange={setCloseRate}
                min={10}
                max={90}
                step={5}
                suffix="%" />

            </div>

            {/* Right — Results */}
            <div className="space-y-4" data-testid="calculator-results">
              {/* Primary result card */}
              <div
                data-testid="result-revenue-lost"
                className="bg-[#0e1e3a] border border-[#1a3a6e] rounded-lg p-6">

                <p className="text-xs text-[#A3A3A3] uppercase tracking-widest mb-2 font-medium" style={{ fontFamily: "Manrope, sans-serif" }}>
                  Annual revenue lost
                </p>
                <p className="text-3xl md:text-4xl font-bold text-[#5B8DEF] mb-1" style={{ fontFamily: "Outfit, sans-serif" }}>
                  ${animRevenue.toLocaleString()}
                </p>
                <p className="text-xs text-[#A3A3A3]" style={{ fontFamily: "Manrope, sans-serif" }}>
                  from missed calls alone
                </p>
              </div>

              {/* Jobs lost card */}
              <div
                data-testid="result-jobs-lost"
                className="bg-[#141414] border border-[#222222] rounded-lg p-6">

                <p className="text-xs text-[#A3A3A3] uppercase tracking-widest mb-2 font-medium" style={{ fontFamily: "Manrope, sans-serif" }}>
                  Jobs lost to competitors per year
                </p>
                <p className="text-2xl font-bold text-white mb-1" style={{ fontFamily: "Outfit, sans-serif" }}>
                  {animJobs.toLocaleString()} jobs
                </p>
                <p className="text-xs text-[#A3A3A3]" style={{ fontFamily: "Manrope, sans-serif" }}>
                  each one booked by someone else
                </p>
              </div>

              {/* Urgency line */}
              <div className="flex items-center gap-2 pt-2" data-testid="urgency-line">
                <span className="relative flex h-2.5 w-2.5">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-60" style={{ backgroundColor: "#c96b4b" }}></span>
                  <span className="relative inline-flex rounded-full h-2.5 w-2.5" style={{ backgroundColor: "#c96b4b" }}></span>
                </span>
                <span className="text-[13px]" style={{ color: "#c96b4b", fontFamily: "Manrope, sans-serif" }}>
                  You're losing ~${animDaily.toLocaleString()} every single day you wait.
                </span>
              </div>

              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-3 pt-3">
                <a
                  href={TRIAL_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="calculator-cta-trial"
                  className="group inline-flex items-center justify-center gap-2 bg-[#5B8DEF] hover:bg-[#4A79D3] text-white font-semibold px-7 py-3 rounded-lg text-sm transition-all duration-200 active:scale-[0.97]"
                  style={{ fontFamily: "Manrope, sans-serif" }}>

                  Start Free 3 Day Trial
                  <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform duration-200" />
                </a>
                <a
                  href={DEMO_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="calculator-cta-demo"
                  className="inline-flex items-center justify-center gap-2 bg-transparent border border-[#2a2a2a] hover:border-white/20 text-[#A3A3A3] hover:text-white font-semibold px-7 py-3 rounded-lg text-sm transition-all duration-200 active:scale-[0.97]"
                  style={{ fontFamily: "Manrope, sans-serif" }}>

                  Book a Demo
                </a>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>);

}